import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Loader2, Download, Receipt } from 'lucide-react';
import { printInvoice } from '../lib/invoicePrint';

const API = process.env.REACT_APP_BACKEND_URL;

export default function PublicInvoicePage() {
  const { billId } = useParams();
  const [bill, setBill] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    axios.get(`${API}/api/chemist/public-bill/${billId}`)
      .then(res => setBill(res.data))
      .catch(() => setError('Invoice not found or link is invalid'))
      .finally(() => setLoading(false));
  }, [billId]);

  if (loading) return <div className="flex justify-center items-center h-screen"><Loader2 className="w-8 h-8 animate-spin text-teal-600" /></div>;
  if (error) return <div className="flex justify-center items-center h-screen text-slate-500" data-testid="public-invoice-error">{error}</div>;

  return (
    <div className="max-w-lg mx-auto p-4 sm:p-8" data-testid="public-invoice-page">
      <div className="border rounded-xl p-5 shadow-sm bg-white space-y-3 text-sm">
        <div className="flex items-center justify-between border-b pb-3">
          <div className="flex items-center gap-3">
            {bill.vendor_logo && <img src={`${API}${bill.vendor_logo}`} alt="Logo" className="w-12 h-12 object-contain rounded border" />}
            <div>
              <p className="font-bold flex items-center gap-1"><Receipt className="w-4 h-4 text-teal-600" /> {bill.vendor_name}</p>
              <p className="text-xs text-slate-500">{bill.vendor_address}</p>
              <p className="text-xs text-slate-500">GSTIN: {bill.vendor_gst} | Ph: {bill.vendor_phone}</p>
            </div>
          </div>
          <Button size="sm" variant="outline" onClick={() => printInvoice(bill)} data-testid="public-invoice-download-btn">
            <Download className="w-4 h-4 mr-1" /> Download
          </Button>
        </div>
        <div><strong>Invoice:</strong> {bill.invoice_number}</div>
        <div><strong>Date:</strong> {new Date(bill.created_at).toLocaleString()}</div>
        <div><strong>Bill To:</strong> {bill.patient_name} {bill.patient_phone && `| ${bill.patient_phone}`}</div>
        <table className="w-full text-xs border"><thead><tr className="bg-slate-100"><th className="p-1 text-left">Item</th><th className="p-1">Qty</th><th className="p-1">Price</th><th className="p-1">Total</th></tr></thead><tbody>
          {bill.items?.map((item, i) => (
            <tr key={`row-${i}`} className="border-t"><td className="p-1">{item.name}</td><td className="p-1 text-center">{item.quantity}</td><td className="p-1 text-right">{item.selling_price}</td><td className="p-1 text-right">{(item.selling_price * item.quantity).toFixed(2)}</td></tr>
          ))}
        </tbody></table>
        <div className="border-t pt-2 space-y-1">
          <div className="flex justify-between"><span>Subtotal</span><span>{bill.subtotal?.toFixed(2)}</span></div>
          {bill.discount > 0 && <div className="flex justify-between text-red-500"><span>Discount</span><span>-{bill.discount.toFixed(2)}</span></div>}
          <div className="flex justify-between"><span>GST ({bill.gst_percentage}%)</span><span>{bill.gst_amount?.toFixed(2)}</span></div>
          <div className="flex justify-between font-bold text-lg"><span>Total</span><span>Rs. {bill.total?.toFixed(2)}</span></div>
        </div>
      </div>
    </div>
  );
}
